import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, SlidersHorizontal, Loader2 } from 'lucide-react';
import { fetchProperties } from './portalApi';
import type { PropertyCardData, ListMeta } from './portalApi';
import PropertyCard from './PropertyCard';

const TYPES = [
  { value: '', label: 'Todos os tipos' },
  { value: 'APARTMENT', label: 'Apartamento' },
  { value: 'HOUSE', label: 'Casa' },
  { value: 'COMMERCIAL', label: 'Comercial' },
  { value: 'LAND', label: 'Terreno' },
  { value: 'RURAL', label: 'Rural' },
  { value: 'INDUSTRIAL', label: 'Industrial' },
];

export default function PortalListings() {
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState<PropertyCardData[]>([]);
  const [meta, setMeta] = useState<ListMeta | null>(null);
  const [loading, setLoading] = useState(true);

  const purpose = params.get('purpose') || '';
  const type = params.get('type') || '';
  const city = params.get('city') || '';
  const q = params.get('q') || '';
  const minPrice = params.get('minPrice') || '';
  const maxPrice = params.get('maxPrice') || '';
  const page = parseInt(params.get('page') || '1', 10);

  useEffect(() => {
    setLoading(true);
    fetchProperties({ purpose, type, city, q, minPrice, maxPrice, page, limit: 12 })
      .then((r) => {
        setData(r.data);
        setMeta(r.meta);
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [purpose, type, city, q, minPrice, maxPrice, page]);

  function setParam(key: string, value: string) {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    next.delete('page');
    setParams(next);
  }

  function goPage(p: number) {
    const next = new URLSearchParams(params);
    next.set('page', String(p));
    setParams(next);
    window.scrollTo(0, 0);
  }

  const title =
    purpose === 'rent' ? 'Imóveis para Alugar' : purpose === 'sale' ? 'Imóveis à Venda' : 'Todos os Imóveis';

  return (
    <div className="max-w-7xl mx-auto px-4 py-10">
      <h1 className="text-2xl md:text-3xl font-bold text-gray-900">{title}</h1>
      <p className="text-gray-500">{meta ? `${meta.total} imóvel(is) encontrado(s)` : 'Buscando...'}</p>

      {/* Filtros */}
      <div className="mt-6 bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
        <div className="flex items-center gap-2 text-gray-700 mb-3 text-sm font-semibold">
          <SlidersHorizontal className="w-4 h-4" /> Filtros
        </div>
        <div className="grid gap-3 md:grid-cols-3 lg:grid-cols-6">
          <select
            value={purpose}
            onChange={(e) => setParam('purpose', e.target.value)}
            className="px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 outline-none"
          >
            <option value="">Comprar ou alugar</option>
            <option value="sale">Comprar</option>
            <option value="rent">Alugar</option>
          </select>
          <select
            value={type}
            onChange={(e) => setParam('type', e.target.value)}
            className="px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 outline-none"
          >
            {TYPES.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
          <input
            defaultValue={city}
            onBlur={(e) => setParam('city', e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && setParam('city', (e.target as HTMLInputElement).value)}
            placeholder="Cidade/bairro"
            className="px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 outline-none"
          />
          <input
            defaultValue={minPrice}
            onBlur={(e) => setParam('minPrice', e.target.value)}
            placeholder="Preço mín."
            type="number"
            className="px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 outline-none"
          />
          <input
            defaultValue={maxPrice}
            onBlur={(e) => setParam('maxPrice', e.target.value)}
            placeholder="Preço máx."
            type="number"
            className="px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 outline-none"
          />
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              defaultValue={q}
              onBlur={(e) => setParam('q', e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && setParam('q', (e.target as HTMLInputElement).value)}
              placeholder="Palavra-chave"
              className="w-full pl-9 pr-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 outline-none"
            />
          </div>
        </div>
      </div>

      {/* Resultados */}
      {loading ? (
        <div className="py-24 flex items-center justify-center text-gray-400">
          <Loader2 className="w-8 h-8 animate-spin" />
        </div>
      ) : data.length === 0 ? (
        <div className="py-24 text-center text-gray-500">
          <p className="text-lg font-medium">Nenhum imóvel encontrado</p>
          <p className="text-sm">Tente ajustar os filtros da busca.</p>
        </div>
      ) : (
        <>
          <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {data.map((p) => (
              <PropertyCard key={p.id} p={p} />
            ))}
          </div>

          {meta && meta.totalPages > 1 && (
            <div className="mt-10 flex items-center justify-center gap-2">
              <button
                onClick={() => goPage(page - 1)}
                disabled={page <= 1}
                className="px-3 py-2 rounded-lg border border-gray-300 text-sm disabled:opacity-40"
              >
                Anterior
              </button>
              <span className="text-sm text-gray-600 px-2">
                Página {meta.page} de {meta.totalPages}
              </span>
              <button
                onClick={() => goPage(page + 1)}
                disabled={page >= meta.totalPages}
                className="px-3 py-2 rounded-lg border border-gray-300 text-sm disabled:opacity-40"
              >
                Próxima
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
